// SEO / Search screen — Google Search Console 28-day performance.
// Lifted from the old MarketingDashboard's SearchPerformanceCard and given
// its own screen in the redesigned shell. Owns its fetch cycle because it
// also owns the connect / disconnect / refresh flows (the OAuth callback
// redirects land here with ?gsc_connected / ?gsc_error params).

import { useEffect, useState } from 'react';
import { apiFetch } from '../../utils/apiFetch';
import { Card, Eyebrow } from './ui';

export default function SeoView() {
  const [status, setStatus] = useState(null);   // { configured, connected, ... }
  const [report, setReport] = useState(null);   // full report payload
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [err, setErr] = useState(null);

  async function loadStatus() {
    try {
      const r = await apiFetch('/api/gsc/status');
      const j = await r.json();
      setStatus(j);
      return j;
    } catch (e) {
      setErr(e.message);
      return null;
    }
  }

  async function loadReport(force = false) {
    setErr(null);
    if (force) setRefreshing(true); else setLoading(true);
    try {
      const path = force ? '/api/gsc/report/refresh' : '/api/gsc/report';
      const method = force ? 'POST' : 'GET';
      const r = await apiFetch(path, { method });
      const j = await r.json();
      if (!r.ok) throw new Error(j.error || `HTTP ${r.status}`);
      setReport(j);
    } catch (e) {
      setErr(e.message);
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }

  useEffect(() => {
    let cancelled = false;
    (async () => {
      const s = await loadStatus();
      if (cancelled) return;
      if (s?.connected) await loadReport(false);
      else setLoading(false);
    })();
    return () => { cancelled = true; };
  }, []);

  // Pick up ?gsc_connected=true / ?gsc_error=... from the OAuth callback
  // redirect, then strip them from the URL so refreshing the page is clean.
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    if (params.has('gsc_connected') || params.has('gsc_error')) {
      const cleanUrl = window.location.pathname + window.location.hash;
      window.history.replaceState({}, '', cleanUrl);
      loadStatus().then(s => { if (s?.connected) loadReport(false); });
    }
  }, []);

  function handleConnect() {
    window.location.href = '/api/gsc/connect';
  }

  async function handleDisconnect() {
    if (!window.confirm('Disconnect Search Console? You can re-connect anytime.')) return;
    try {
      const r = await apiFetch('/api/gsc/disconnect', { method: 'POST' });
      if (!r.ok) throw new Error('Disconnect failed');
      setReport(null);
      setStatus({ ...status, connected: false });
    } catch (e) { setErr(e.message); }
  }

  // Not configured (env vars missing on server)
  if (!loading && status && status.configured === false) {
    return (
      <Card className="p-5 border-dashed max-w-[720px]">
        <Eyebrow className="mb-2">Search performance · 28 days</Eyebrow>
        <div className="text-[12px] text-ink-2">
          GSC env vars not set. Add <code className="font-mono bg-bone/60 px-1 rounded">GSC_CLIENT_ID</code>,{' '}
          <code className="font-mono bg-bone/60 px-1 rounded">GSC_CLIENT_SECRET</code>,{' '}
          <code className="font-mono bg-bone/60 px-1 rounded">GSC_REDIRECT_URI</code>, and{' '}
          <code className="font-mono bg-bone/60 px-1 rounded">GSC_SITE_URL</code> in Vercel.
        </div>
      </Card>
    );
  }

  // Configured but not connected — show Connect button
  if (!loading && status && status.connected === false) {
    return (
      <Card className="p-5 border-dashed max-w-[720px]">
        <Eyebrow className="mb-2">Search performance · 28 days</Eyebrow>
        <div className="text-[13px] text-ink-2 mb-3">
          Connect Google Search Console to populate impressions, clicks, top queries, top pages,
          and surprise queries (positions 11-30, the quick-win bucket).
        </div>
        <button
          onClick={handleConnect}
          className="text-[11px] font-bold px-3 py-1.5 rounded-[6px] bg-bullogic-blue text-white hover:bg-bullogic-blue/90"
        >
          Connect Search Console
        </button>
        {err && <div className="text-[11px] text-rose mt-2">{err}</div>}
      </Card>
    );
  }

  return (
    <Card lash="blue" className="p-5">
      <div className="flex items-center justify-between mb-3">
        <div>
          <Eyebrow>Search performance · 28 days</Eyebrow>
          {/* Which Google account authorized. A 403 from GSC or GA4 is
              almost always the wrong account, so this is worth showing. */}
          {status?.connectedBy && (
            <div className="text-[11px] text-ink-3 mt-0.5">
              Connected as <span className="font-bold text-ink-2">{status.connectedBy}</span>
            </div>
          )}
        </div>
        <div className="flex gap-2">
          <button
            onClick={() => loadReport(true)}
            disabled={refreshing || loading}
            className={`text-[10px] font-bold px-2 py-0.5 rounded-[5px] ${
              refreshing || loading
                ? 'bg-line-2 text-ink-3 cursor-not-allowed'
                : 'bg-bullogic-blue/10 text-bullogic-blue hover:bg-bullogic-blue/20'
            }`}
          >
            {refreshing ? 'Refreshing…' : 'Refresh'}
          </button>
          <button
            onClick={handleConnect}
            className="text-[10px] font-bold px-2 py-0.5 rounded-[5px] bg-bullogic-blue/10 text-bullogic-blue hover:bg-bullogic-blue/20"
            title="Re-authorize, choosing which Google account to connect"
          >
            Reconnect
          </button>
          <button
            onClick={handleDisconnect}
            className="text-[10px] font-bold px-2 py-0.5 rounded-[5px] bg-rose/10 text-rose hover:bg-rose/20"
            title="Disconnect Search Console"
          >
            Disconnect
          </button>
        </div>
      </div>

      {loading && !report && <div className="text-[12px] text-ink-3">Loading from Search Console…</div>}
      {err && <div className="text-[12px] text-rose">Failed: {err}</div>}

      {report && report.connected && (
        <>
          {/* Headline numbers */}
          <div className="grid grid-cols-4 gap-3 mb-4">
            <Stat label="Clicks" value={report.summary?.clicks?.toLocaleString() || '0'} subtitle={report.cached ? 'cached' : 'live'} />
            <Stat label="Impressions" value={report.summary?.impressions?.toLocaleString() || '0'} />
            <Stat label="CTR" value={`${((report.summary?.ctr || 0) * 100).toFixed(1)}%`} />
            <Stat label="Avg Position" value={(report.summary?.position || 0).toFixed(1)} />
          </div>

          <GscTable
            title="Top Queries"
            rows={report.topQueries || []}
            keyFn={(r, i) => r.query + i}
            cols={[
              { label: 'Query', render: r => <span className="truncate" title={r.query}>{r.query}</span>, flex: true },
              { label: 'Clicks', render: r => r.clicks, w: 'w-12 text-right tabular-nums' },
              { label: 'Impr.', render: r => r.impressions?.toLocaleString(), w: 'w-16 text-right tabular-nums' },
              { label: 'Pos', render: r => r.position?.toFixed(1), w: 'w-12 text-right tabular-nums' },
            ]}
            emptyMsg="No query data yet."
          />

          <GscTable
            title="Top Pages"
            rows={report.topPages || []}
            keyFn={(r, i) => r.page + i}
            cols={[
              { label: 'Page', render: r => <span className="truncate font-mono" title={r.page}>{shortPath(r.page)}</span>, flex: true },
              { label: 'Clicks', render: r => r.clicks, w: 'w-12 text-right tabular-nums' },
              { label: 'Impr.', render: r => r.impressions?.toLocaleString(), w: 'w-16 text-right tabular-nums' },
              { label: 'Pos', render: r => r.position?.toFixed(1), w: 'w-12 text-right tabular-nums' },
            ]}
            emptyMsg="No page data yet."
          />

          <GscTable
            title="Surprise Queries (positions 11-30, quick-win opportunities)"
            rows={report.surpriseQueries || []}
            keyFn={(r, i) => r.query + i}
            cols={[
              { label: 'Query', render: r => <span className="truncate" title={r.query}>{r.query}</span>, flex: true },
              { label: 'Impr.', render: r => r.impressions?.toLocaleString(), w: 'w-16 text-right tabular-nums' },
              { label: 'Pos', render: r => r.position?.toFixed(1), w: 'w-12 text-right tabular-nums' },
            ]}
            emptyMsg="No surprise queries yet. Comes back populated once you have impressions in the 11-30 position range."
          />
        </>
      )}
    </Card>
  );
}

function Stat({ label, value, subtitle }) {
  return (
    <div>
      <div className="text-[9px] font-black uppercase tracking-[0.1em] text-ink-3">{label}</div>
      <div className="text-[24px] font-black text-navy leading-tight tabular-nums">{value}</div>
      {subtitle && <div className="text-[10px] text-ink-3 mt-0.5">{subtitle}</div>}
    </div>
  );
}

function GscTable({ title, rows, cols, keyFn, emptyMsg }) {
  return (
    <div className="mt-4">
      <div className="text-[10px] font-black uppercase tracking-[0.08em] text-ink-3 mb-2">{title}</div>
      {rows.length === 0 ? (
        <div className="text-[11px] text-ink-3 italic">{emptyMsg}</div>
      ) : (
        <div>
          <div className="flex items-center gap-3 text-[9px] font-black uppercase tracking-[0.08em] text-ink-3 pb-1 mb-1 border-b border-line-2">
            {cols.map(c => (
              <div key={c.label} className={c.flex ? 'flex-1 min-w-0' : `${c.w}`}>
                {c.label}
              </div>
            ))}
          </div>
          <div className="space-y-1">
            {rows.map((r, i) => (
              <div key={keyFn(r, i)} className="flex items-center gap-3 text-[12px]">
                {cols.map(c => (
                  <div key={c.label} className={c.flex ? 'flex-1 min-w-0 text-navy' : `${c.w} text-ink-2`}>
                    {c.render(r)}
                  </div>
                ))}
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

function shortPath(url) {
  try {
    const u = new URL(url);
    return u.pathname || '/';
  } catch {
    return url;
  }
}
